<template>
    <div>
        <div class="form-group !m-0 w-full relative" :class="[hasErrorProp ? 'has-error' : '', mainWrapperClass]">
            <div>
                <label v-if="label" :for="name" :class="['font-normal !m-0', labelClass]">
                    {{ label }} <span v-if="required" :class="['!text-[#FE5000]',labelRequiredColor]">*</span>
                    <span v-if="spanText"
                        class="text-[12px] 2xl:text-[16px] font-roboto !font-[400] text-[#999999]">(Max 45
                        characters allowed.)</span>
                </label>
                <span v-if="spanTextDescription"
                    :class="['block text-[12px] 2xl:text-[14px] font-roboto !font-[400] text-[#999999]', spanTextClass]">{{
                        description }}</span>
            </div>
            <div class="relative">
                <div
                    :class="['relative flex items-center rounded-[10px] border-[1px] border-[#EFEFEF] bg-[#fff]', mainInputWrapper]">
                    <span v-if="currency"
                        class="flex items-center justify-center w-[40px] h-[35px] 2xl:h-[37px] z-[9] font-roboto text-[18px] 2xl:text-[20px] rounded-l-[7px] 2xl:rounded-l-[10px] text-[#fff] bg-[#F5B134]">{{
                            currency }}</span>
                    <input :type="inputType" :name="name" :id="name" v-model="localValue" :placeholder="placeholder"
                        :class="['mt-0 !pt-2 !pb-2 !pl-2 !pr-2  rounded-[10px] focus:outline-none ', customClass]"
                        :style="computedStyles" @input="onInput" @blur="onBlur" :disabled="disabled"
                        @paste="disablePaste" @keypress="isNumber($event)" autocomplete="new-password" @drop.prevent
                        @dragover.prevent />
                </div>
                <!-- Error -->
                <div v-if="hasErrorProp"
                    :class="['!text-[#f00] text-[11px] absolute -bottom-5 left-0', customErrorClass]">
                    <div v-if="errors.required">This field is required.</div>
                    <div v-if="errors.pattern">{{ validation.message || 'Invalid format.' }}</div>
                    <div v-if="errors.custom">{{ errors.custom }}</div>
                    <div v-if="errors.range">Value out of range.</div>
                </div>
            </div>
        </div>

    </div>
</template>

<script setup>
import { ref, watch, computed, defineProps, defineEmits, defineExpose, nextTick } from 'vue';
import debounce from 'lodash/debounce';

const props = defineProps({
    modelValue: {
        type: [String, Number],
        default: ""
    },
    value: { // Backward compatibility
        type: [String, Number],
        default: ""
    },
    paddingLeft: {
        type: Number,
        default: 8,
    },
    margin: {
        type: Number,
    },
    width: {
        type: Number,
        default: 100,
    },
    textCenter: {
        type: String,
        default: ""
    },
    label: {
        type: String,
        default: ""
    },
    description: {
        type: String,
        default: ""
    },
    spanTextClass: {
        type: String,
        default: ""
    },
    currency: {
        type: String,
        default: "$"
    },
    type: {
        type: String,
        default: "text"
    },
    name: {
        type: String,
        required: false
    },
    placeholder: {
        type: String,
        default: ""
    },
    required: {
        type: Boolean,
        default: false
    },
    spanText: {
        type: Boolean,
        default: false
    },
    spanTextDescription: {
        type: Boolean,
        default: false
    },
    disabled: {
        type: Boolean,
        default: false
    },
    validation: {
        type: Object,
        default: () => ({})
    },
    fieldType: {
        type: String,
        default: null
    },
    maxLength: {
        type: [String, Number],
        default: null
    },
    minValue: {
        type: Number,
        default: null
    },
    maxValue: {
        type: Number,
        default: null
    },
    labelClass: {
        type: String,
        default: '!mb-2 text-sm 2xl:text-md font-medium text-customGray-light'
    },
    labelRequiredColor: {
        type: String,
        default: ''
    },
    mainWrapperClass: {
        type: [String, Object, Array],
        default: ''
    },
    mainInputWrapper: {
        type: String,
        default: ''
    },
    customClass: {
        type: String,
        default: 'text-sm 2xl:text-md w-full'
    },
    customErrorClass: {
        type: String,
        default: ''
    },
    pasteDisabled: {
        type: Boolean,
        default: false
    },
    numericAngleClass: {
        type: String,
        default: 'hidden'
    }
});

const emit = defineEmits(['update:modelValue', 'input', 'validation']);

const localValue = ref(props.modelValue !== undefined ? props.modelValue : props.value);
const skipValidation = ref(false);
const showPassword = ref(false);
const errors = ref({
    required: false,
    pattern: false,
    custom: "",
    range: false
});

// Watch triggers
watch(() => props.modelValue, (newVal) => {
    if (newVal !== localValue.value) {
        localValue.value = newVal;
        validateField();
    }
}, { immediate: true });

// Support for old 'value' prop if modelValue is not used
watch(() => props.value, (newVal) => {
    if (props.modelValue === undefined && newVal !== localValue.value) {
        localValue.value = newVal;
        validateField();
    }
});

const inputType = computed(() => {
    if (props.type === "password") {
        return showPassword.value ? "text" : "password";
    }
    return props.type;
});

const hasErrorProp = computed(() => {
    return (
        errors.value.required ||
        errors.value.pattern ||
        !!errors.value.custom ||
        errors.value.range
    );
});

const computedStyles = computed(() => {
    return {
        paddingLeft: `${props.paddingLeft}px !important`,
        width: `${props.width}% !important`,
        margin: `${props.margin}px !important`,
        textAlign: props.textCenter ? "center" : "left",
    };
});

function togglePassword() {
    showPassword.value = !showPassword.value;
}

const debouncedValidate = debounce(validateField, 300);

function onInput() {
    if (props.fieldType === 'phone') {
        acceptNumberLogin();
    }

    if (['float', 'number'].includes(props.fieldType)) {
        let val = localValue.value.toString().replace(/[^\d.]/g, "");
        let numVal = props.fieldType === 'float' ? parseFloat(val) : parseInt(val);

        if (props.minValue !== null && numVal < props.minValue) {
            localValue.value = props.minValue.toString();
        } else if (props.maxValue !== null && numVal > props.maxValue) {
            localValue.value = props.maxValue.toString();
        }
    }

    emit("update:modelValue", localValue.value);
    emit("input", localValue.value);
    debouncedValidate();
}

function onBlur() {
    validateField();
}

function resetField() {
    showPassword.value = false;
    skipValidation.value = true;
    errors.value.required = false;
    errors.value.pattern = false;
    errors.value.custom = "";
    errors.value.range = false;
    nextTick(() => {
        skipValidation.value = false;
    });
}

function validateField() {
    if (skipValidation.value) return;

    // Reset Errors
    errors.value.required = false;
    errors.value.pattern = false;
    errors.value.custom = "";
    errors.value.range = false;

    // **Required Field Check**
    if (props.required && (localValue.value == null || (localValue.value + "").trim() === "")) {
        errors.value.required = true;
    }

    // **Pattern Validation Check**
    else if (props.validation.pattern && localValue.value != null && !props.validation.pattern.test(localValue.value)) {
        errors.value.pattern = true;
    }

    // **Custom Validation Function Check**
    else if (props.validation.custom && typeof props.validation.custom === "function") {
        const customError = props.validation.custom(localValue.value);
        if (customError) {
            errors.value.custom = customError;
        }
    }

    // ✅ Min/Max Value Validation for number or float
    else if (['number', 'float'].includes(props.fieldType) && localValue.value !== "") {
        const numericValue = parseFloat(localValue.value.toString().replace(/[^\d.]/g, ""));
        if (
            (props.minValue !== null && numericValue < props.minValue) ||
            (props.maxValue !== null && numericValue > props.maxValue)
        ) {
            errors.value.range = true;
        }
    }

    // **Emit validation event for parent component**
    emit("validation", {
        name: props.name,
        errors: { ...errors.value },
        hasError: hasErrorProp.value
    });

    return hasErrorProp.value;
}

function acceptNumberLogin() {
    if (props.fieldType && props.fieldType == 'phone') {
        var x = localValue.value
            .replace(/\D/g, "")
            .match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
        localValue.value = !x[2] ?
            x[1] :
            "(" + x[1] + ") " + x[2] + (x[3] ? "-" + x[3] : "");
    }
}

function disablePaste(event) {
    const clipboardData = event.clipboardData || window.clipboardData;
    const pastedText = clipboardData.getData('text');
    if (props.fieldType && (props.fieldType == 'float' || props.fieldType == 'number' || props.fieldType == 'password')) {
        event.preventDefault();
    } else if (props.pasteDisabled) {
        event.preventDefault();
    } else if (props.maxLength) {
        event.preventDefault(); // stop default paste

        const input = event.target;
        const currentValue = input.value;
        const selectionStart = input.selectionStart;
        const selectionEnd = input.selectionEnd;

        // Replace selected text with pasted text
        const before = currentValue.slice(0, selectionStart);
        const after = currentValue.slice(selectionEnd);
        let newValue = before + pastedText + after;

        // Enforce max length
        if (newValue.length > props.maxLength) {
            newValue = newValue.slice(0, props.maxLength);
        }

        // Set manually
        localValue.value = newValue;
        emit('update:modelValue', localValue.value);
        emit('input', localValue.value);
    }
}

function isNumber(evt) {
    evt = evt || window.event;
    var charCode = evt.which ? evt.which : evt.keyCode;
    var value = evt.target.value || '';
    let inputChar = String.fromCharCode(charCode);

    if (props.type === 'password' && inputChar === ' ') {
        evt.preventDefault();
        return false;
    }

    if (props.fieldType === 'number' || props.fieldType === 'float') {
        var maxLength = props.maxLength || 10;
        // Always allow control keys
        if ([8, 9, 27, 13].includes(charCode)) return true;
        if (evt.ctrlKey && [65, 67, 88, 86].includes(charCode)) return true;

        let nextValue = value + inputChar;

        // Allow only digits and handle fieldType
        if (props.fieldType === 'number') {
            if (!/^\d*$/.test(inputChar)) return evt.preventDefault();

            // Check length
            if (nextValue.length > maxLength) return evt.preventDefault();

            // Check min/max restriction
            const numericVal = parseInt(nextValue);
            if (props.minValue !== null && numericVal < props.minValue) return evt.preventDefault();
            if (props.maxValue !== null && numericVal > props.maxValue) return evt.preventDefault();

            return true;
        }

        if (props.fieldType === 'float') {
            if (!/^\d*\.?\d*$/.test(inputChar)) return evt.preventDefault();
            var dotPosition = value.indexOf('.');
            var hasDot = dotPosition !== -1;
            var isDecimalKey = (charCode === 46 || charCode === 110);

            // Decimal point handling
            if (isDecimalKey) {
                if (hasDot) return evt.preventDefault(); // Block second dot
                return true; // Always allow 1st dot
            }

            // Digits handling
            if ((charCode >= 48 && charCode <= 57) || (charCode >= 96 && charCode <= 105)) {
                if (hasDot) {
                    var parts = value.split('.');
                    var decimalPart = parts[1] || '';
                    if (decimalPart.length >= 2) return evt.preventDefault(); // Max 2 digits after dot
                    return true;
                } else {
                    if (value.length >= maxLength) return evt.preventDefault(); // Only block if no dot and max length reached
                    return true;
                }
            }
            return evt.preventDefault(); // Block other keys
        }

    } else {
        let maxLength = props.maxLength || 255;
        let nextValue = value + inputChar;
        if (nextValue.length > maxLength) return evt.preventDefault();
    }
    return true;
}

defineExpose({
    resetField,
    validateField,
    hasError: hasErrorProp
});
</script>

<style scoped>
/* .has-error input {
  border-color: red;
} */
/* .text-danger {
  color: red;
} */
</style>
